import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, defer, Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HydratedSmartFilter } from '../../../../models/automation/hydrated-smart-filter';
import { BaseModalViewModel } from '../../../../models/base/base-modal-view-model';
import { SmartFiltersDomainModel } from '../../../../domainModels/smart-filters-domain-model';
import { ConfirmationOptions } from '../../../../models/shared/stylesheet/confirmation-options';
import { AddEditSmartFilterModalOpenedFrom } from '../../../../models/automation/enum/add-edit-smart-filter-modal-opened-from';
import { exists } from '../../../../functions/exists';
import { ModalConfirmation } from '../../../../modals/modal-confirmation';
import { iiif$ } from '../../../../utils/observable.extensions';
import { GradingProvider } from '../../../../models/enum/dto/grading-provider-enum';
import { MinMaxValue } from '../../../../models/product/dto/min-max-value';
import { ToastService } from '../../../../modules/toasts/service/toast.service';
import { ProductGroupDomainModel } from '../../../../domainModels/product-group-domain-model';

@Injectable()
export class AddEditSmartFilterViewModel extends BaseModalViewModel {
  constructor(
    protected activeModal: NgbActiveModal,
    private productGroupDomainModel: ProductGroupDomainModel,
    private smartFiltersDomainModel: SmartFiltersDomainModel,
    private toastService: ToastService,
    router: Router,
    ngbModal: NgbModal,
    private injector: Injector
  ) {
    super(router, ngbModal);
  }

  // Edit Form
  private _canSubmitForm = new BehaviorSubject<boolean>(false);
  public canSubmitForm$ = this._canSubmitForm as Observable<boolean>;
  connectToCanSubmit = (canSubmit: boolean) => this._canSubmitForm.next(canSubmit);

  public _smartFilterReq = new BehaviorSubject<HydratedSmartFilter>(new HydratedSmartFilter());
  public smartFilterReq$ = this._smartFilterReq as Observable<HydratedSmartFilter>;

  public _existingSmartFilter = new BehaviorSubject<HydratedSmartFilter>(null);
  public existingSmartFilter$ = this._existingSmartFilter as Observable<HydratedSmartFilter>;
  connectToExistingSmartFilter = (smartFilter: HydratedSmartFilter) => this._existingSmartFilter.next(smartFilter);

  public hasSystemIdentifier$ = this.existingSmartFilter$.pipe(
    map(smartFilter => exists(smartFilter?.systemIdentifier))
  );

  private _isEditing = new BehaviorSubject<boolean>(false);
  public isEditing$ = this._isEditing as Observable<boolean>;
  connectToIsEditing = (isEditing: boolean) => this._isEditing.next(isEditing);

  // Accordion
  private _openProductAccordion = new BehaviorSubject<boolean>(false);
  public openProductAccordion$ = this._openProductAccordion as Observable<boolean>;

  private _openCannabinoidAccordion = new BehaviorSubject<boolean>(false);
  public openCannabinoidAccordion$ = this._openCannabinoidAccordion as Observable<boolean>;

  private _openTerpeneAccordion = new BehaviorSubject<boolean>(false);
  public openTerpeneAccordion$ = this._openTerpeneAccordion as Observable<boolean>;

  private _openAdvancedAccordion = new BehaviorSubject<boolean>(false);
  public openAdvancedAccordion$ = this._openAdvancedAccordion as Observable<boolean>;

  // Opened From
  public _openedFrom = new BehaviorSubject<AddEditSmartFilterModalOpenedFrom>(null);
  public openedFrom$ = this._openedFrom as Observable<AddEditSmartFilterModalOpenedFrom>;
  connectToOpenedFrom = (openedFrom: AddEditSmartFilterModalOpenedFrom) => this._openedFrom.next(openedFrom);

  listenForProperties = this.existingSmartFilter$.notNull().subscribeWhileAlive({
    owner: this,
    next: sf => {
      if (sf?.hasProductPropertySet()) this._openProductAccordion.next(true);
      if (sf?.hasCannabinoidPropertySet()) this._openCannabinoidAccordion.next(true);
      if (sf?.hasTerpenePropertySet()) this._openTerpeneAccordion.next(true);
      if (sf?.hasAdvancedPropertySet()) this._openAdvancedAccordion.next(true);
    }
  });

  public readonly bindTo$ = combineLatest([
    iiif$(this.isEditing$, this.existingSmartFilter$, this.smartFilterReq$),
    window.types.gradingProviders$
  ]).pipe(
    map(([smartFilter, gradingProviders]) => {
      if (smartFilter && gradingProviders?.length > 0) {
        smartFilter.gradingResults = smartFilter.gradingResults || new Map<GradingProvider, MinMaxValue>();
        gradingProviders.forEach(provider => {
          const key = provider.value;
          if (!smartFilter.gradingResults.has(key)) {
            smartFilter.gradingResults.set(key, new MinMaxValue());
          }
        });
      }
      return smartFilter;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  saveChanges() {
    this.existingSmartFilter$.once(existingSmartFilter => {
      this.createOrUpdateSmartFilter(existingSmartFilter);
    });
  }

  private createOrUpdateSmartFilter(existingSmartFilter: HydratedSmartFilter) {
    combineLatest([this.smartFilterReq$, this.isEditing$])
      .pipe(
        take(1),
        switchMap(([smartFilterReq, isEditing]) => {
          this.addLoadingReq();
          const update$ = defer(() => this.smartFiltersDomainModel.updateSmartFilter$(existingSmartFilter));
          const create$ = defer(() => this.smartFiltersDomainModel.createSmartFilter$(smartFilterReq));
          return isEditing ? update$ : create$;
        }),
        take(1)
      )
      .subscribe({
        next: returnedSmartFilter => {
          this.removeLoadingReq();
          const successMsg = this._isEditing.getValue()
            ? 'Your smart filter was updated'
            : 'Your smart filter was created';
          this.toastService.publishSuccessMessage(successMsg, 'Success!');
          this.activeModal.close(returnedSmartFilter);
        },
        error: err => {
          this.removeLoadingReq();
          this.toastService.publishErrorMessage(err, 'Error');
        }
      });
  }

  showDeleteModal() {
    this.existingSmartFilter$.once(sf => {
      const opts = new ConfirmationOptions();
      opts.title = 'DANGER ZONE: Delete Smart Filter';
      opts.bodyText =
        `DANGER. Are you sure you want to delete ${sf.name}? ` +
        'Deleting a smart filter will affect all customers that are using it!';
      opts.cancelText = 'Cancel';
      opts.continueText = 'Continue';
      const confirmation = (confirm: boolean) => {
        if (confirm) {
          this.deleteSmartFilter();
        }
      };
      ModalConfirmation.open$(this.ngbModal, this.injector, opts, confirmation);
    });
  }

  deleteSmartFilter(force: boolean = false) {
    const lm = 'Deleting your Smart Filter';
    const smartFilter = this._existingSmartFilter.getValue();
    this._loadingOpts.addRequest(lm);
    this.smartFiltersDomainModel.deleteSmartFilter$(smartFilter, force).subscribe({
      next: () => {
        this.toastService.publishSuccessMessage('Your Smart Filter was deleted', 'Smart Filters');
        this._loadingOpts.removeRequest(lm);
        this.activeModal.close();
      },
      error: err => {
        if (err?.code === 409) {
          this._loadingOpts.removeRequest(lm);
          this.existingSmartFilter$.once(sf => {
            const opts = new ConfirmationOptions();
            opts.title = 'DANGER ZONE: Delete Smart Filter';
            opts.bodyText = err?.message;
            opts.cancelText = 'Cancel';
            opts.continueText = 'Delete Anyway';
            const confirmation = (confirm: boolean) => {
              if (confirm) {
                this.deleteSmartFilter(true);
              }
            };
            ModalConfirmation.open$(this.ngbModal, this.injector, opts, confirmation);
          });
          return;
        }
        this.toastService.publishError(err);
        this._loadingOpts.removeRequest(lm);
      }
    });
  }

  setCanSubmit(canSubmit: boolean) {
    this._canSubmitForm.next(canSubmit);
  }

  setFormObject(hydratedFilters: HydratedSmartFilter[]) {
    this.isEditing$.once(isEditing => {
      isEditing
        ? this._existingSmartFilter.next(hydratedFilters?.firstOrNull())
        : this._smartFilterReq.next(hydratedFilters?.firstOrNull());
    });
  }

  addLoadingReq() {
    this.isEditing$.once(isEditing => {
      const lm = isEditing ? 'Saving Changes' : 'Creating Smart Filter';
      this._loadingOpts.addRequest(lm);
    });
  }

  removeLoadingReq() {
    this.isEditing$.once(isEditing => {
      const lm = isEditing ? 'Saving Changes' : 'Creating Smart Filter';
      this._loadingOpts.removeRequest(lm);
    });
  }
}
